import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Captcha, CaptchaHandle, useToast } from '@app/core';
import { ApiFormattedException, useSendEmailValidationCode } from '@app/api';
import { CodeInput } from '@app/form';

import Mail from '@app/illu/Large/Mail.svg';

const useStyles = createUseStyles((theme: Theme) => ({
  title: {
    ...theme.fonts.h3,
  },
  text: {
    ...theme.fonts.body,
    color: theme.colors.inkSecondary,
    marginTop: theme.marginBase * 4,
    marginBottom: theme.marginBase * 4,
  },
  error: {
    ...theme.fonts.body,
    textAlign: 'center',
    color: theme.colors.redAccessible,
  },
  resend: {
    ...theme.fonts.caption,
    marginTop: theme.marginBase * 4,
    textAlign: 'center',
  },
  resendLink: {
    color: theme.colors.blueAccessible,
    fontWeight: 'bold',
  },
  illustration: {
    margin: 'auto',
    width: '100%',
    maxWidth: 116,
  },
  illustrationContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
}));

interface Props {
  hideTitle?: boolean;
  displayed?: boolean;
  email?: string;
  participantId?: string;
  verifiedMethod: (accountId: string, codeRef: string) => any;
  text?: string;
  isLoading?: boolean;
  error: Error | ApiFormattedException | null;
  sentText?: string;
  onNoAccountForParticipant?: () => boolean | undefined | void;
}

export const CodeVerificationContent = ({
  email,
  hideTitle,
  participantId,
  displayed,
  text,
  isLoading,
  error,
  verifiedMethod,
  sentText,
  onNoAccountForParticipant,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation('common');
  const toast = useToast();

  const [touched, setTouched] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);
  const codeRef = useRef('');
  const captchaRef = useRef<CaptchaHandle>(null);
  const [solvingCaptcha, setSolvingCaptcha] = useState(false);

  const res = useSendEmailValidationCode();
  const { mutateAsync: sendValidationCode, isLoading: sendingCode, data } = res;
  const { accountId } = data || {};

  const resend = useCallback(async () => {
    try {
      setTouched(false);
      formRef.current?.reset();
      setSolvingCaptcha(true);
      const altcha = await captchaRef.current?.getPayload().finally(() => {
        setSolvingCaptcha(false);
      });
      await sendValidationCode({ participantId, email, altcha: altcha || '' });
      toast.success(sentText || t('login.emailValidationCode.sent'));
    } catch (e) {
      if (
        e instanceof ApiFormattedException &&
        e.getCode() === 'NO_ACCOUNT_FOR_PARTICIPANT_ID' &&
        onNoAccountForParticipant
      ) {
        if (!onNoAccountForParticipant()) {
          return;
        }
      }
      toast.exception(e);
    }
  }, [sendValidationCode, participantId, email, toast, t, sentText]);

  const displayRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (displayRef.current === displayed) {
      return;
    }
    displayRef.current = displayed;
    if (!email && !participantId) {
      return;
    }
    if (!displayed) {
      return;
    }
    setTimeout(async () => {
      await resend();
    });
  }, [email, participantId, displayed, resend, displayRef]);

  const onLetterChanged = useCallback(
    (letters: string) => {
      setTouched(true);
      codeRef.current = letters;
      if (letters.length === 6) {
        setTimeout(() => {
          if (formRef.current) {
            formRef.current.dispatchEvent(
              new Event('submit', { cancelable: true, bubbles: true }),
            );
          }
        });
      }
    },
    [codeRef, formRef],
  );

  const submit = useCallback(
    async (e: FormEvent) => {
      try {
        e.preventDefault();
        e.stopPropagation();
        setTouched(false);
        if (!accountId) {
          toast.exception();
          return;
        }
        await verifiedMethod(accountId, codeRef.current);
      } catch (e) {
        toast.exception(e);
      }
    },
    [accountId, verifiedMethod, toast],
  );

  return (
    <>
      <form onSubmit={submit} ref={formRef}>
        {!hideTitle && (
          <h1 className={classes.title}>
            {t('login.emailValidationCode.title')}
          </h1>
        )}
        <div className={classes.illustrationContainer}>
          <img src={Mail} className={classes.illustration} alt="Email" />
        </div>
        <p className={classes.text}>
          {text ? text : t('login.emailValidationCode.text')}
        </p>
        <CodeInput ariaLabel="Code" length={6} onChange={onLetterChanged} />

        {!touched && error && 'getTranslatedMessage' in error && (
          <p className={classes.error}>{error.getTranslatedMessage()}</p>
        )}

        <div className={classes.resend}>
          <Trans
            i18nKey="login.emailValidationCode.resend"
            components={{
              a: (
                <button
                  type="button"
                  onClick={resend}
                  className={classes.resendLink}
                  disabled={isLoading || sendingCode || solvingCaptcha}
                />
              ),
            }}
          />
        </div>
      </form>
      {/* Outside of the form: an invisible widget would otherwise hook the code submission */}
      <Captcha ref={captchaRef} invisible />
    </>
  );
};
